// src/context/EmpresaContext.jsx
import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { onIdTokenChanged } from 'firebase/auth';
import { auth, db } from '../firebaseConfig';
import { doc, onSnapshot, getDoc } from 'firebase/firestore';

const EmpresaContext = createContext({
  empresaId: null,
  loading: true,
  refreshClaims: async () => null,
});

export const useEmpresaId = () => useContext(EmpresaContext).empresaId;
/** Use este hook se quiser acessar também loading/refreshClaims */
export const useEmpresa = () => useContext(EmpresaContext);

export function EmpresaProvider({ children }) {
  const [empresaId, setEmpresaId] = useState(localStorage.getItem('empresaId') || null);
  const [loading, setLoading] = useState(true);

  // Aplica empresaId no estado e no localStorage
  const applyEmpresaId = (id) => {
    const val = id || null;
    setEmpresaId(val);
    if (val) localStorage.setItem('empresaId', val);
    else localStorage.removeItem('empresaId');
  };

  // Força pegar claims novos (use após sua callable atualizarMinhasClaims)
  const refreshClaims = async (timeoutMs = 5000) => {
    const user = auth.currentUser;
    if (!user) {
      applyEmpresaId(null);
      return null;
    }
    const t0 = Date.now();
    while (Date.now() - t0 < timeoutMs) {
      await user.getIdToken(true); // força refresh
      const token = await user.getIdTokenResult();
      const claimEmpresa = token?.claims?.empresaId || null;
      if (claimEmpresa) {
        applyEmpresaId(claimEmpresa);
        return token.claims;
      }
      await new Promise((r) => setTimeout(r, 250));
    }
    // Fallback: lê o perfil no Firestore
    try {
      const snap = await getDoc(doc(db, 'users', user.uid));
      const id = snap.exists() ? snap.data()?.empresaId || null : null;
      applyEmpresaId(id);
      return { empresaId: id };
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let unsubUser = null;

    const unsubAuth = onIdTokenChanged(auth, async (user) => {
      // encerra qualquer listener anterior do perfil
      if (unsubUser) {
        unsubUser();
        unsubUser = null;
      }

      if (!user) {
        applyEmpresaId(null);
        setLoading(false);
        return;
      }

      // tenta pegar empresaId pelos claims já no login
      try {
        await user.getIdToken(true);
        const token = await user.getIdTokenResult();
        if (token?.claims?.empresaId) applyEmpresaId(token.claims.empresaId);
      } catch (_) {}

      // escuta em tempo real o doc users/{uid}
      const ref = doc(db, 'users', user.uid);
      unsubUser = onSnapshot(
        ref,
        (snap) => {
          const id = snap.exists() ? snap.data()?.empresaId || null : null;
          applyEmpresaId(id);
          setLoading(false);
        },
        (_err) => {
          // se der erro no snapshot, pelo menos não travamos a UI
          setLoading(false);
        }
      );

      // opcional: um get inicial para evitar qualquer atraso até o 1º snapshot
      try {
        const first = await getDoc(ref);
        if (first.exists()) applyEmpresaId(first.data()?.empresaId || null);
      } catch (_) {}
    });

    return () => {
      unsubAuth && unsubAuth();
      unsubUser && unsubUser();
    };
  }, []);

  const value = useMemo(
    () => ({ empresaId, loading, refreshClaims }),
    [empresaId, loading]
  );

  return (
    <EmpresaContext.Provider value={value}>
      {!loading && children}
    </EmpresaContext.Provider>
  );
}
